import type {
  CanvasDocumentMountIdentity,
  CanvasDocumentPresentationSnapshot,
  CanvasDocumentScopeSnapshot,
} from './canvasDocumentScope';
import type { CanvasDraftSessionController } from './canvasDraftController';
import type {
  CanvasSessionCallbackIdentity,
  CanvasSessionCheckpoint,
  CanvasSessionRegistrySnapshot,
  CanvasSessionSnapshot,
  CanvasSessionTransition,
} from './canvasSessionRegistry';
import { isCanvasSessionProtected } from './canvasSessionRegistry';

export interface CanvasResidentSeed {
  readonly documentId: string | null;
  readonly presentation: CanvasDocumentPresentationSnapshot | null;
  /** Draft already fetched by the capacity transaction for this exact mount. */
  readonly hydratedDraftId?: string | null;
}

/**
 * Une demande de résidence vise une CONVERSATION (#1336).
 *
 * ⚠️ Il n'y a plus de `documentSessionId` : la conversation EST la clé du
 * document logique, et l'époque de montage vit dans le registre — la demande ne
 * la porte pas, puisqu'elle demande précisément qu'un montage ait lieu. Le
 * champ n'est pas non plus nullable : on n'indexe pas une collection par `null`.
 */
export interface CanvasResidentRequest {
  readonly conversationId: string;
  /** Required only for a document that has never owned a durable draft. */
  readonly seed?: CanvasResidentSeed;
  /** Background jobs warm their exact editor without stealing the lawyer's visible host. */
  readonly visibility: 'select' | 'preserve';
  /** Cancels an admission which has not yet started a physical mount. */
  readonly signal?: AbortSignal;
  /**
   * Ce qui reste du budget du demandeur, lu quand l'attente du montage à froid
   * commence (après le téléchargement de la cible et le checkpoint d'une
   * victime, qui l'ont consommé). Remplace `DEFAULT_HYDRATION_TIMEOUT_MS` pour
   * ce montage : l'admission d'un travail de rédaction a 30 s côté backend. La
   * restauration d'un sortant après échec garde la borne par défaut : elle ne
   * sert pas ce demandeur.
   */
  readonly hydrationBudgetMs?: () => number;
  /**
   * `seed` REPLACES a retained opening that never became ready, instead of that
   * opening being retried or the request being refused for lack of a draft.
   *
   * Only for a caller that wants a document INDEPENDENT of whatever this
   * conversation tried to open before — today the agent's blank page
   * (`open_canvas_document`, source blank). Without it, a cold load whose DOCX
   * could not mount stays the conversation's retained opening, and the blank
   * page the agent falls back to fails in turn (measured 2026-10-09): either
   * the same unmountable source is retried (no editor was ever exposed), or the
   * request is refused with "no durable draftId" (an editor was created and the
   * mount then failed — the real case, a Word file whose `doc.info` throws).
   *
   * The condition is "never ready, never modified", NOT "never exposed"
   * (`CanvasResidenceCapacityPort.openingNeverBecameReady` + an untouched draft
   * controller). Nothing of the lawyer's can be lost: a mount that never became
   * ready was never writable. A document that was once ready — including an
   * editor that crashed after edits — still requires its exact durable draft.
   */
  readonly seedReplacesUnmountedOpening?: boolean;
}

export interface CanvasResidentDecision {
  readonly accepted: true;
  readonly status: 'already-hot' | 'hydrated';
  /** Conversation dont le document a cédé sa place physique, le cas échéant. */
  readonly cooledConversationId: string | null;
  readonly session: CanvasDocumentScopeSnapshot;
}

export interface CanvasResidenceCapacityOptions {
  readonly maxHotSessions?: number;
  readonly loadDraftContent?: (draftId: string) => Promise<Blob>;
  readonly hydrationTimeoutMs?: number;
}

/**
 * Tout ce dont la capacité a besoin du runtime, adressé par CONVERSATION.
 *
 * Ce module n'importe pas le hub runtime : c'est son implémentation qui lui est
 * injectée. C'est ce qui rend la capacité testable au seam public sans monter ni
 * registre ni SuperDoc — ne pas remplacer ce port par un import direct.
 */
export interface CanvasResidenceCapacityPort {
  getRegistrySnapshot(): CanvasSessionRegistrySnapshot;
  getScopeSnapshot(conversationId: string): CanvasDocumentScopeSnapshot | null;
  getDraftController(conversationId: string): CanvasDraftSessionController | null;
  getLogicalSeed(conversationId: string): CanvasResidentSeed | null;
  canReuseOriginalSeed(conversationId: string): boolean;
  /**
   * No mount of this conversation's document ever reached `ready`, and its
   * draft controller never moved — whether or not an editor was exposed.
   *
   * Weaker than `canReuseOriginalSeed` on purpose: an editor that was created
   * and then failed before `ready` (a DOCX whose `doc.info` throws) was never
   * writable by the lawyer, the agent or a job, so nothing can be lost by
   * mounting something else. It does NOT make the retained opening retryable.
   */
  openingNeverBecameReady(conversationId: string): boolean;
  transition(conversationId: string, transition: CanvasSessionTransition): CanvasSessionSnapshot | null;
  touch(conversationId: string): CanvasSessionSnapshot | null;
  mountColdTarget(input: {
    conversationId: string;
    seed: CanvasResidentSeed;
  }): CanvasDocumentScopeSnapshot;
  coolPhysical(conversationId: string, reason: string): boolean;
  waitForExactReady(identity: CanvasDocumentMountIdentity, timeoutMs: number): Promise<CanvasDocumentScopeSnapshot>;
  selectVisible(conversationId: string): void;
  maskVisible(conversationId: string): void;
}

/**
 * Le sortant EXACT qu'un geste de sélection s'est autorisé à refroidir.
 *
 * C'est une identité de MONTAGE, pas un document : le même document remonté
 * entre-temps n'est plus ce sortant-là. Depuis #1336 cette identité est
 * exactement celle du registre — l'aliaser plutôt que la redéclarer interdit aux
 * deux formes de diverger le jour où l'identité changera.
 */
type ExactOutgoingResident = CanvasSessionCallbackIdentity;

interface ResidenceIntent {
  visibility: 'select' | 'preserve';
  outgoing: ExactOutgoingResident | null;
  revision: number;
}

/**
 * L'opération de résidence en cours — il n'y en a jamais qu'UNE (#1338).
 *
 * Elle remplace une file (des demandes en attente, un drain en microtâche, un
 * tri « sélection d'abord, puis séquence ») qui ne contenait jamais plus d'une
 * entrée : toute demande froide est refusée dès qu'une autre transaction est
 * ouverte, sauf une jonction. Aucune de ses lignes ne décidait. Un champ unique
 * rend l'invariant « une transition à la fois » structurel au lieu de le
 * laisser déduire d'un refus placé ailleurs.
 */
interface ResidenceOperation {
  readonly request: CanvasResidentRequest;
  readonly intent: ResidenceIntent;
  /**
   * Le document que cette opération monte RÉELLEMENT, dans cet ordre :
   * 1. la graine REMPLAÇANTE (`replacementSeed`) quand la demande en déclare une
   *    et qu'elle s'applique — l'opération ne porte alors PAS le document que le
   *    fil retenait, et une demande concurrente de ce document ne doit pas la
   *    joindre : elle recevrait la session d'une page vierge ;
   * 2. sinon la graine logique de la conversation quand elle existe —
   *    `preloadTarget` ignore alors toute graine de demande ;
   * 3. sinon la graine de la demande qui l'a ouverte.
   * `null` : aucune identité prouvable (document vierge, Blob importé), donc
   * jamais jointe par une demande qui apporte une source.
   */
  readonly sourceDocumentId: string | null;
  /**
   * La graine montée À LA PLACE de l'ouverture retenue, décidée une seule fois
   * à l'ouverture de l'opération (`seedReplacingRetainedOpening`) : la source
   * annoncée aux jonctions et la graine préchargée ne peuvent pas diverger.
   */
  readonly replacementSeed: CanvasResidentSeed | null;
  readonly promise: Promise<CanvasResidentDecision>;
  /**
   * Se résout quand la transaction s'est DÉNOUÉE et a rendu sa place — succès,
   * échec ou abandon. Jamais rejetée : c'est un signal d'ordonnancement, pas un
   * verdict.
   */
  readonly settled: Promise<void>;
  abandoned: boolean;
  abandon(reason: unknown): void;
}

/**
 * Ce qu'une demande APPORTE à monter, pour juger une jonction (#1338, D-F).
 *
 * ⚠️ **Trois cas, pas deux** : ne rien apporter, apporter une source sans
 * identité, apporter une source identifiée. Replier le premier sur le second
 * ferait de chaque clic un « document vierge » jamais joignable.
 */
type SourceApportee =
  | { readonly apporte: false }
  | { readonly apporte: true; readonly documentId: string | null };

/**
 * La graine vierge `{ documentId: null, presentation: null }` n'est PAS une
 * source : c'est le repli des gestes « ouvre le document de cette
 * conversation » (`CanvasResidence`, l'admission d'un fil froid), qui ne décrit
 * qu'un tout premier montage. La traiter en document vierge jamais joignable
 * tuerait la promotion préserver → afficher, qui joint justement le
 * réchauffement de l'admission et le clic de l'avocat — deux graines vierges.
 */
function sourceApportee(request: CanvasResidentRequest): SourceApportee {
  const seed = request.seed;
  if (!seed || (seed.documentId === null && seed.presentation === null)) return { apporte: false };
  return { apporte: true, documentId: seed.documentId };
}

interface ResidenceVictim {
  readonly session: CanvasSessionSnapshot;
  readonly visibleAuthorization: ExactOutgoingResident | null;
}

interface CheckpointRetryReservation {
  readonly victimConversationId: string;
  readonly draftId: string;
  readonly version: number;
  readonly visibleAuthorization: ExactOutgoingResident | null;
}

/**
 * Enveloppe de résidence de production : **trois** éditeurs.
 *
 * Décision D-A du porteur (#1338, 2026-09-10), prise en connaissance de son
 * coût : l'enveloppe était de quatre — trois documents d'agent protégés plus le
 * document manuel courant de l'avocat — et le parcours du **quatrième document
 * manuel** (#1050) disparaît avec ce plafond.
 *
 * ⚠️ **Le défaut est jugé par une garde dédiée**
 * (`canvasDocumentRuntime.test.ts`, « refuse un QUATRIÈME résident sur le
 * plafond par défaut »). Les gardes qui passent `maxHotSessions` en option
 * restent vertes quel que soit ce défaut : c'est ce qui a laissé le plafond à
 * quatre sans qu'aucun rouge ne le signale.
 */
const DEFAULT_MAX_HOT_SESSIONS = 3;
/**
 * Enveloppe d'attente d'une hydratation physique (montage ou remontage).
 *
 * Elle doit couvrir la construction SuperDoc la plus lente observée tout en
 * restant strictement SOUS le budget que le backend accorde au geste qui l'a
 * demandée : sinon le tool abandonne pendant que le navigateur construit
 * encore, et l'éditeur qui finit par arriver n'a plus personne pour l'accuser.
 *
 * Le geste le plus serré qui traverse cette enveloppe est le chargement d'un
 * document : `LoadDocumentIntoCanvas.ts` attend
 * `CANVAS_LOAD_TEMPLATE_ACK_TIMEOUT_MS` (`@hector/shared-types`). Les 14 s laissent donc au
 * navigateur le temps de construire ET à l'accusé le temps de repartir.
 *
 * ⚠️ Les bornes de `SuperDocCanvasTools.ts` (`INSPECT_TIMEOUT_MS`, 45 s ;
 * `MUTATION_TIMEOUT_MS`, 20 s) ne bornent PAS ce chemin — elles gouvernent
 * `canvas_inspect` / `canvas_perform_action`. L'affirmation inverse a circulé
 * dans deux commentaires ; ne pas la rétablir. En sens inverse, la borne de
 * lecture est dimensionnée sur cette enveloppe (8 s de recherche du brouillon
 * + ces 14 s + 14 s d'attente d'éditeur prêt = 36 s, sous 45 s) : la relever
 * oblige à relire `INSPECT_TIMEOUT_MS`.
 *
 * Exportée parce que `WebSocketPool` borne son REMONTAGE sur exactement la même
 * enveloppe (un remontage coûte ce que coûte un montage à froid). Recopier la
 * valeur à la main la laisserait diverger en silence.
 */
export const DEFAULT_HYDRATION_TIMEOUT_MS = 14_000;

/** A request abandoned by its owner cannot mount later. */
export class CanvasResidenceAbandonedError extends Error {
  constructor(conversationId: string, reason: unknown) {
    super(
      `Canvas residence request abandoned for conversationId=${conversationId}: ${String(reason ?? 'the caller no longer needs this editor')}.`,
    );
    this.name = 'CanvasResidenceAbandonedError';
  }
}

export type CanvasResidenceUnavailableKind = 'capacity' | 'transition' | 'invalid';

/** No physical editor can be assigned without displacing protected work. */
export class CanvasResidenceUnavailableError extends Error {
  constructor(
    conversationId: string,
    readonly kind: CanvasResidenceUnavailableKind,
    reason: string,
  ) {
    super(
      `Canvas residence unavailable for conversationId=${conversationId}: ${reason}.`,
    );
    this.name = 'CanvasResidenceUnavailableError';
  }
}

/**
 * Le contenu exact d'un brouillon DÉCOUVERT n'a pas pu être téléchargé (#1332).
 *
 * Distinguée des indisponibilités de capacité parce qu'elle ne se répare pas de
 * la même façon et ne se dit pas de la même façon : ici l'éditeur était
 * disponible, c'est le document qui manque. Elle porte le `draftId` pour que
 * l'appelant puisse publier un tri-état honnête plutôt qu'un toast générique —
 * et surtout pour interdire les deux replis destructeurs, la page blanche et
 * l'original importé.
 */
export class CanvasDraftContentUnreadableError extends Error {
  /**
   * La cause du téléchargement seule, sans la consigne qui l'entoure : un
   * appelant qui rédige son propre refus (`WebSocketPool`) la cite sans
   * recopier une seconde fois « do not open a blank canvas ».
   */
  readonly detail: string;

  constructor(
    readonly conversationId: string,
    readonly draftId: string,
    cause: unknown,
  ) {
    const detail = cause instanceof Error ? cause.message : String(cause);
    super(
      `Canvas draft content unreadable for draftId=${draftId} `
      + `conversationId=${conversationId}: `
      + `${detail}. `
      + 'The lookup already proved this draft exists: do not open a blank canvas and do not fall '
      + 'back to the imported original; retry this same residence request.',
    );
    this.name = 'CanvasDraftContentUnreadableError';
    this.detail = detail;
  }
}

/**
 * Serializes the only transaction allowed to exchange a physical residence.
 *
 * One exact opening may be in flight (`current`), and this coordinator
 * deliberately does not retain a queue of future openings. A lawyer who
 * changes conversation needs a prompt verdict, not a stale spinner that may
 * outlive their gesture.
 * Editor mutations remain scoped and concurrent. Every await is followed by
 * identity/activity revalidation before the first irreversible step
 * (`coolPhysical`).
 */
export class CanvasResidenceCapacityCoordinator {
  private current: ResidenceOperation | null = null;
  /** Indexée par la conversation CIBLE du retry (#1336). */
  private readonly checkpointRetryByTarget = new Map<string, CheckpointRetryReservation>();
  private readonly maxHotSessions: number;
  private readonly loadDraftContent: (draftId: string) => Promise<Blob>;
  private readonly hydrationTimeoutMs: number;

  constructor(
    private readonly port: CanvasResidenceCapacityPort,
    options: CanvasResidenceCapacityOptions = {},
  ) {
    this.maxHotSessions = options.maxHotSessions ?? DEFAULT_MAX_HOT_SESSIONS;
    this.loadDraftContent = options.loadDraftContent ?? (() => Promise.reject(new Error(
      'Canvas cold hydration has no draft loader. Configure loadDraftContent before requesting a cold resident.',
    )));
    this.hydrationTimeoutMs = options.hydrationTimeoutMs ?? DEFAULT_HYDRATION_TIMEOUT_MS;
  }

  requestResident(request: CanvasResidentRequest): Promise<CanvasResidentDecision> {
    let target: CanvasSessionSnapshot;
    try {
      target = this.requireTarget(request);
    } catch (error) {
      return Promise.reject(error);
    }
    if (request.seedReplacesUnmountedOpening && !request.seed) {
      // Sans ce refus, la déclaration serait ignorée et l'ouverture retenue
      // rejouée en silence : l'appelant croirait avoir posé SON document.
      return Promise.reject(this.unavailable(
        request,
        'invalid',
        'seedReplacesUnmountedOpening was set without a seed: the request declares a replacement and '
        + 'brings nothing to mount. Pass the seed to mount, or drop the flag to open the retained document',
      ));
    }
    const current = this.current;
    if (current?.request.conversationId === request.conversationId) {
      if (request.seedReplacesUnmountedOpening) {
        // Une graine déclarée remplaçante ne JOINT pas et n'est pas refusée :
        // elle attend le dénouement de l'opération en vol, puis redemande pour
        // elle-même. Joindre lui rendrait la session d'une autre source ; la
        // refuser (`transition`) ferait échouer la page neuve de l'agent parce
        // qu'un brouillon se restaurait ou qu'un chargement montait encore
        // (régression du 2026-10-09). Rien n'est perdu : après un succès le
        // document du fil est chaud et la demande le trouve tel quel
        // (`already-hot`) ; après un échec, elle monte sa graine si l'ouverture
        // n'a jamais été prête, ou réhydrate le brouillon durable.
        return current.settled.then(() => this.requestResident(request));
      }
      const refus = this.refuseIncompatibleJoin(current, request);
      if (refus) return Promise.reject(refus);
      this.promoteIntent(current.intent, request);
      return current.promise;
    }
    const intent = this.createIntent(request);
    if (target.temperature === 'hot') {
      const scope = this.port.getScopeSnapshot(target.conversationId);
      if (!scope || scope.state !== 'ready') {
        return Promise.reject(this.unavailable(request, 'invalid', 'the registry is hot but the exact editor is not ready'));
      }
      this.port.touch(target.conversationId);
      if (intent.visibility === 'select') this.port.selectVisible(target.conversationId);
      return Promise.resolve({ accepted: true, status: 'already-hot', cooledConversationId: null, session: scope });
    }
    if (target.temperature === 'hydrating') {
      return Promise.reject(this.unavailable(
        request,
        'invalid',
        'the document is hydrating without a live request to join; retry the opening gesture',
      ));
    }
    if (current) {
      return Promise.reject(this.unavailable(
        request,
        'transition',
        'another document is changing editor residency; retry once that opening completes',
      ));
    }
    const occupied = this.port.getRegistrySnapshot().sessions
      .filter((session) => session.temperature === 'hot' || session.temperature === 'hydrating');
    if (
      occupied.length >= this.maxHotSessions
      && !this.checkpointRetryByTarget.has(target.conversationId)
      && !this.chooseVictim(target.conversationId, intent)
    ) {
      return Promise.reject(this.unavailable(
        request,
        'capacity',
        `all ${this.maxHotSessions} editors are occupied by active or protected documents`,
      ));
    }
    return this.openOperation(request, intent);
  }

  reset(): void {
    this.checkpointRetryByTarget.clear();
    this.current?.abandon('the canvas runtime was reset');
  }

  /**
   * Une transaction de résidence est-elle en vol pour CE fil (#1586) ?
   *
   * Lue par la restauration d'un brouillon et par l'amorce d'une admission
   * agent, AVANT de préparer une session logique : la poser pendant que la
   * transaction monte ferait diverger ce qu'elle monte de ce que le fil porte.
   * Une opération abandonnée compte encore — elle garde sa place jusqu'à ce que
   * sa transaction se dénoue (voir `openOperation`).
   */
  aUneTransactionEnVolPour(conversationId: string): boolean {
    return this.current?.request.conversationId === conversationId;
  }

  /**
   * Refuse de joindre une opération qui monte une AUTRE source (#1338, D-F).
   *
   * Compatible = même conversation ET même source, l'identité d'une source
   * étant son `documentId`. Sans ce refus, la jonction ne regardait que la
   * conversation : la seconde source recevait la session de la première, son
   * DOCX était jeté, et seule la postcondition d'identité du chargement agent
   * (`WebSocketPool`) le découvrait — après toute l'attente. Une source sans
   * `documentId` n'est jamais prouvable identique, donc jamais jointe.
   */
  private refuseIncompatibleJoin(
    operation: ResidenceOperation,
    request: CanvasResidentRequest,
  ): CanvasResidenceUnavailableError | null {
    const apportee = sourceApportee(request);
    if (!apportee.apporte) return null;
    if (apportee.documentId !== null && apportee.documentId === operation.sourceDocumentId) return null;
    return this.unavailable(
      request,
      'transition',
      'another opening of this conversation is in flight on a different source '
      + `(inFlightDocumentId=${operation.sourceDocumentId ?? 'none'} `
      + `requestedDocumentId=${apportee.documentId ?? 'none'}); two sources are never merged and a source `
      + 'without documentId cannot be proven identical. Nothing was mounted for this request: retry once '
      + 'that opening completes',
    );
  }

  private openOperation(
    request: CanvasResidentRequest,
    intent: ResidenceIntent,
  ): Promise<CanvasResidentDecision> {
    if (request.signal?.aborted) {
      return Promise.reject(new CanvasResidenceAbandonedError(request.conversationId, request.signal.reason));
    }
    let resolveOperation!: (decision: CanvasResidentDecision) => void;
    let rejectOperation!: (error: Error) => void;
    const promise = new Promise<CanvasResidentDecision>((resolve, reject) => {
      resolveOperation = resolve;
      rejectOperation = reject;
    });
    let markSettled!: () => void;
    const settled = new Promise<void>((resolve) => { markSettled = resolve; });
    const logical = this.port.getLogicalSeed(request.conversationId);
    const replacementSeed = this.seedReplacingRetainedOpening(request);
    const operation: ResidenceOperation = {
      request,
      intent,
      sourceDocumentId: replacementSeed
        ? replacementSeed.documentId
        : logical ? logical.documentId : request.seed?.documentId ?? null,
      replacementSeed,
      promise,
      settled,
      abandoned: false,
      abandon: (reason) => {
        if (operation.abandoned) return;
        operation.abandoned = true;
        rejectOperation(new CanvasResidenceAbandonedError(request.conversationId, reason));
      },
    };
    const onAbort = () => operation.abandon(request.signal?.reason);
    request.signal?.addEventListener('abort', onAbort, { once: true });
    this.current = operation;
    void this.transact(request, intent, operation).then(
      (decision) => {
        if (!operation.abandoned) resolveOperation(decision);
      },
      (error: unknown) => {
        if (!operation.abandoned) rejectOperation(error instanceof Error ? error : new Error(String(error)));
      },
    ).finally(() => {
      request.signal?.removeEventListener('abort', onAbort);
      // L'opération garde sa place jusqu'à ce que sa transaction se dénoue, MÊME
      // abandonnée : elle ne s'arrête qu'à son prochain `throwIfAbandoned`, et
      // libérer la place avant laisserait deux transactions échanger des
      // éditeurs en même temps.
      if (this.current === operation) this.current = null;
      markSettled();
    });
    return promise;
  }

  private async transact(
    request: CanvasResidentRequest,
    intent: ResidenceIntent,
    operation: ResidenceOperation,
  ): Promise<CanvasResidentDecision> {
    this.throwIfAbandoned(operation);
    const target = this.requireTarget(request);
    if (target.temperature === 'hot') {
      const scope = this.port.getScopeSnapshot(target.conversationId);
      if (!scope || scope.state !== 'ready') {
        throw this.error(request, null, 'registry says hot but its ready physical scope is missing');
      }
      this.port.touch(target.conversationId);
      if (intent.visibility === 'select') this.port.selectVisible(target.conversationId);
      return { accepted: true, status: 'already-hot', cooledConversationId: null, session: scope };
    }
    if (target.temperature === 'hydrating') {
      throw this.error(request, null, 'target is hydrating without a joinable transaction');
    }

    const seed = await this.preloadTarget(request, target, operation.replacementSeed);
    this.throwIfAbandoned(operation);
    this.requireUnchangedTarget(request, target);

    const counted = this.port.getRegistrySnapshot().sessions
      .filter((session) => session.temperature === 'hot' || session.temperature === 'hydrating');
    const retryReservation = this.checkpointRetryByTarget.get(target.conversationId) ?? null;
    let victim = counted.length >= this.maxHotSessions
      ? retryReservation
        ? this.requireRetryVictim(request, retryReservation, intent)
        : this.chooseVictim(target.conversationId, intent)
      : null;
    if (counted.length >= this.maxHotSessions && !victim) {
      throw this.error(
        request,
        null,
        `capacity=${this.maxHotSessions} has no hot masked unpinned quiescent persistable victim`,
      );
    }

    while (victim) {
      const selectedIntentRevision = intent.revision;
      const cooled = await this.checkpointAndCool(
        request,
        target,
        victim,
        intent,
        selectedIntentRevision,
      );
      this.throwIfAbandoned(operation);
      if (cooled) break;
      // A human selection may join while a background victim is awaiting its
      // checkpoint. The completed checkpoint stays useful, but only the exact
      // outgoing resident captured by the promoted intent may now be cooled.
      victim = this.chooseVictim(target.conversationId, intent);
      if (!victim) {
        throw this.error(
          request,
          null,
          `capacity=${this.maxHotSessions} has no victim matching the promoted selection intent`,
        );
      }
    }
    this.throwIfAbandoned(operation);
    this.requireUnchangedTarget(request, target);

    const mounted = this.port.mountColdTarget({
      conversationId: request.conversationId,
      seed,
    });
    this.port.transition(request.conversationId, { axis: 'temperature', value: 'hydrating' });
    let ready: CanvasDocumentScopeSnapshot;
    try {
      ready = await this.port.waitForExactReady(mounted, request.hydrationBudgetMs?.() ?? this.hydrationTimeoutMs);
    } catch (error) {
      this.port.coolPhysical(request.conversationId, 'cold hydration failed');
      this.port.transition(request.conversationId, { axis: 'temperature', value: 'cold' });
      let rollbackError: unknown = null;
      if (victim) {
        try {
          await this.restoreCooledResident(victim.session);
        } catch (failure) {
          rollbackError = failure;
        }
      }
      throw this.error(
        request,
        this.port.getDraftController(request.conversationId)?.getSnapshot().draftId ?? null,
        rollbackError
          ? `hydration failed on mountEpoch=${mounted.mountEpoch} and the outgoing resident rollback failed; retry`
          : `hydration failed on mountEpoch=${mounted.mountEpoch}; the outgoing resident was restored, retry the same document`,
        rollbackError ?? error,
      );
    }
    if (operation.abandoned || request.signal?.aborted) {
      this.port.coolPhysical(request.conversationId, 'cancelled residence hydration');
      this.port.transition(request.conversationId, { axis: 'temperature', value: 'cold' });
      this.throwIfAbandoned(operation);
    }
    this.port.transition(request.conversationId, { axis: 'temperature', value: 'hot' });
    this.port.touch(request.conversationId);
    if (intent.visibility === 'select') this.port.selectVisible(request.conversationId);
    return {
      accepted: true,
      status: 'hydrated',
      cooledConversationId: victim?.session.conversationId ?? null,
      session: ready,
    };
  }

  private throwIfAbandoned(operation: ResidenceOperation): void {
    if (operation.abandoned || operation.request.signal?.aborted) {
      throw new CanvasResidenceAbandonedError(
        operation.request.conversationId,
        operation.request.signal?.reason,
      );
    }
  }

  private createIntent(request: CanvasResidentRequest): ResidenceIntent {
    return {
      visibility: request.visibility,
      outgoing: request.visibility === 'select' ? this.captureOutgoing(request.conversationId) : null,
      revision: 0,
    };
  }

  private promoteIntent(intent: ResidenceIntent, request: CanvasResidentRequest): void {
    if (request.visibility !== 'select' || intent.visibility === 'select') return;
    // A human gesture can promote a background warm-up, but a later background
    // caller can never demote the lawyer's selection intent.
    intent.visibility = 'select';
    intent.outgoing = this.captureOutgoing(request.conversationId);
    intent.revision += 1;
  }

  private captureOutgoing(targetConversationId: string): ExactOutgoingResident | null {
    const registry = this.port.getRegistrySnapshot();
    const outgoingId = registry.visibleConversationId;
    if (!outgoingId || outgoingId === targetConversationId) return null;
    const outgoing = registry.sessions.find((session) => session.conversationId === outgoingId);
    return outgoing ? {
      conversationId: outgoing.conversationId,
      mountEpoch: outgoing.mountEpoch,
    } : null;
  }

  /**
   * La conversation demandée possède-t-elle un document logique ?
   *
   * ⚠️ **Il n'y a plus qu'une issue de refus** (#1336). La seconde comparait le
   * propriétaire de la session trouvée à celui qu'annonçait la demande ; la
   * conversation étant désormais la clé de la collection, cette comparaison
   * rendrait la même valeur avec le défaut et sans lui — la tautologie que le
   * noyau a retirée de `decideCallback` pour la même raison.
   */
  private requireTarget(request: CanvasResidentRequest): CanvasSessionSnapshot {
    const target = this.port.getRegistrySnapshot().sessions
      .find((session) => session.conversationId === request.conversationId);
    if (!target) throw this.error(request, null, 'logical session was not found');
    return target;
  }

  private requireUnchangedTarget(request: CanvasResidentRequest, expected: CanvasSessionSnapshot): void {
    const live = this.requireTarget(request);
    if (
      live.mountEpoch !== expected.mountEpoch
      || live.temperature !== expected.temperature
      || live.pinCount !== expected.pinCount
    ) {
      throw this.error(
        request,
        null,
        `target changed while capacity awaited: expectedEpoch=${expected.mountEpoch} `
        + `liveEpoch=${live.mountEpoch} expectedTemperature=${expected.temperature} `
        + `liveTemperature=${live.temperature}; retry the residence request`,
      );
    }
  }

  private async preloadTarget(
    request: CanvasResidentRequest,
    target: CanvasSessionSnapshot,
    replacementSeed: CanvasResidentSeed | null,
  ): Promise<CanvasResidentSeed> {
    const logicalSeed = this.port.getLogicalSeed(target.conversationId);
    if (!logicalSeed) {
      if (request.seed) return request.seed;
      throw this.error(request, null, 'first cold mount has no seed; retry from the original opening gesture');
    }
    const controller = this.port.getDraftController(target.conversationId);
    const before = controller?.getSnapshot() ?? null;
    if (replacementSeed) return replacementSeed;
    if (
      this.draftNeverMoved(before)
      && this.port.canReuseOriginalSeed(target.conversationId)
    ) {
      // A mount that never exposed an editor cannot have unsaved editor work.
      // Retry its retained opening, never a caller's undeclared seed. All
      // previously editable documents still require the exact durable draft.
      return logicalSeed;
    }
    if (!controller || !before?.draftId) {
      throw this.error(
        request,
        before?.draftId ?? null,
        'cold document has no durable draftId and cannot be rehydrated; retry after saving it',
      );
    }
    let blob: Blob;
    try {
      blob = await this.loadDraftContent(before.draftId);
    } catch (error) {
      // ⚠️ Erreur TYPÉE, et pas un message parmi d'autres : le tri-état de
      // restauration doit pouvoir distinguer « je n'ai pas pu lire ce contenu »
      // d'un refus de capacité. Un 404 arrive ici APRÈS que la recherche a
      // affirmé l'existence du brouillon ; le confondre avec une absence
      // ouvrirait la page blanche que la première frappe écraserait.
      throw new CanvasDraftContentUnreadableError(
        request.conversationId,
        before.draftId,
        error,
      );
    }
    const after = controller.getSnapshot();
    if (
      after.draftId !== before.draftId
      || after.conversationId !== before.conversationId
      || after.version !== before.version
    ) {
      throw this.error(
        request,
        before.draftId,
        `draft identity changed during GET (liveDraftId=${after.draftId ?? 'none'} liveVersion=${after.version}); retry`,
      );
    }
    return {
      ...logicalSeed,
      hydratedDraftId: before.draftId,
      presentation: {
        ...(logicalSeed.presentation ?? {
          templateMeta: null,
          documentTitle: null,
          activeDraft: null,
          draftNeedsHydration: false,
        }),
        templateOverride: blob,
        draftNeedsHydration: false,
      },
    };
  }

  /** No draft identity, no captured edit, no save in flight or failed. */
  private draftNeverMoved(
    draft: ReturnType<CanvasDraftSessionController['getSnapshot']> | null,
  ): boolean {
    return draft !== null
      && draft.draftId === null
      && draft.version === 0
      && draft.savedVersion === 0
      && draft.status === 'idle';
  }

  /**
   * The seed a declared replacement mounts INSTEAD of the conversation's
   * retained opening, or `null` when the retained document must be served.
   *
   * Each condition is load-bearing (one guard per condition in
   * `canvasResidenceCapacity.test.ts`):
   * - the request declares it (`seedReplacesUnmountedOpening`) — otherwise a
   *   caller's seed never displaces what the conversation holds;
   * - the draft controller never moved — a draftId, a version or a pending
   *   save is the lawyer's work and is rehydrated from the server;
   * - no mount ever became ready — a document that was once writable may hold
   *   edits no draft captured (editor crash), so it is never replaced by a
   *   blank here; the explicit replacement after residence handles it.
   */
  private seedReplacingRetainedOpening(request: CanvasResidentRequest): CanvasResidentSeed | null {
    if (!request.seedReplacesUnmountedOpening || !request.seed) return null;
    const draft = this.port.getDraftController(request.conversationId)?.getSnapshot() ?? null;
    if (!this.draftNeverMoved(draft)) return null;
    if (!this.port.openingNeverBecameReady(request.conversationId)) return null;
    return request.seed;
  }

  private chooseVictim(
    targetConversationId: string,
    intent: ResidenceIntent,
  ): ResidenceVictim | null {
    const outgoing = intent.visibility === 'select' ? intent.outgoing : null;
    if (outgoing) {
      const exactVisible = this.port.getRegistrySnapshot().sessions.find(
        (session) => session.conversationId === outgoing.conversationId,
      );
      if (
        exactVisible
        && exactVisible.conversationId !== targetConversationId
        && this.hasEligibleResidence(exactVisible, ['clean', 'dirty'], outgoing)
      ) {
        return { session: exactVisible, visibleAuthorization: outgoing };
      }
    }
    const candidates = this.port.getRegistrySnapshot().sessions.filter((session) => {
      return session.conversationId !== targetConversationId
        && this.hasEligibleResidence(session, ['clean', 'dirty']);
    });
    // ⚠️ **Le départage des égalités LRU est le MONTAGE LE PLUS ANCIEN** (#1336).
    //
    // `lastUsedSequence` est un ordinal d'onglet unique par `touch` : deux
    // résidents ne peuvent l'avoir égal que s'il vaut `0`, c'est-à-dire s'ils
    // n'ont JAMAIS été utilisés depuis leur acquisition. Le critère de secours
    // décide donc lequel de deux résidents également inutilisés cède sa place —
    // ce n'est pas un détail de tri, c'est un comportement d'éviction.
    //
    // Jusqu'à ce lot il était LEXICOGRAPHIQUE sur l'UUID du document
    // (`documentSessionId.localeCompare`) : un ordre stable mais sans aucun sens
    // métier, qui désignait la victime au hasard de l'alphabet. Le remplacer par
    // `conversationId.localeCompare` aurait gardé ce hasard en changeant
    // seulement de tirage — ce que le ticket interdit explicitement.
    //
    // `mountEpoch` est le bon critère et il est GRATUIT : compteur monotone
    // d'onglet alloué à chaque montage, donc jamais partagé par deux résidents
    // chauds. Il rend l'ordre TOTAL (aucun troisième niveau n'est nécessaire) et
    // il prolonge l'intention du premier critère — à défaut de savoir lequel a
    // servi le plus récemment, on refroidit celui qui occupe une place depuis le
    // plus longtemps. Garde : `canvasResidenceCapacity.test.ts`, « le départage
    // LRU refroidit le montage le plus ancien ».
    candidates.sort((a, b) => (
      a.lastUsedSequence - b.lastUsedSequence
      || a.mountEpoch - b.mountEpoch
    ));
    const session = candidates[0] ?? null;
    return session ? { session, visibleAuthorization: null } : null;
  }

  private requireRetryVictim(
    request: CanvasResidentRequest,
    reservation: CheckpointRetryReservation,
    intent: ResidenceIntent,
  ): ResidenceVictim {
    const victim = this.port.getRegistrySnapshot().sessions.find(
      (session) => session.conversationId === reservation.victimConversationId,
    );
    const controller = victim
      ? this.port.getDraftController(victim.conversationId)?.getSnapshot() ?? null
      : null;
    const visibleAuthorization = reservation.visibleAuthorization
      && intent.visibility === 'select'
      && this.sameOutgoing(reservation.visibleAuthorization, intent.outgoing)
      ? reservation.visibleAuthorization
      : null;
    const stillReserved = victim
      && (!reservation.visibleAuthorization || visibleAuthorization)
      && this.hasEligibleResidence(victim, ['error'], visibleAuthorization)
      && controller?.draftId === reservation.draftId
      && controller.version === reservation.version;
    if (!stillReserved) {
      throw this.error(
        request,
        reservation.draftId,
        `checkpoint retry reservation for victimConversationId=${reservation.victimConversationId} version=`
        + `${reservation.version} is no longer quiescent and exact; keep it open and retry later`,
      );
    }
    return { session: victim, visibleAuthorization };
  }

  private async checkpointAndCool(
    request: CanvasResidentRequest,
    target: CanvasSessionSnapshot,
    selectedVictim: ResidenceVictim,
    intent: ResidenceIntent,
    selectedIntentRevision: number,
  ): Promise<boolean> {
    const { session: victim, visibleAuthorization } = selectedVictim;
    const controller = this.port.getDraftController(victim.conversationId);
    if (!controller) throw this.error(request, null, `victimConversationId=${victim.conversationId} lost its draft controller`);
    const before = controller.getSnapshot();
    if (!before.draftId) {
      throw this.error(
        request,
        null,
        `victimConversationId=${victim.conversationId} lost its durable draft before checkpoint; keep it open and retry`,
      );
    }
    this.port.transition(victim.conversationId, { axis: 'checkpoint', value: 'checkpointing' });
    try {
      const receipt = await controller.checkpoint();
      const after = controller.getSnapshot();
      this.port.transition(victim.conversationId, {
        axis: 'checkpoint',
        value: after.savedVersion >= after.version ? 'clean' : 'dirty',
      });
      this.requireUnchangedTarget(request, target);
      const liveVictim = this.port.getRegistrySnapshot().sessions
        .find((session) => session.conversationId === victim.conversationId);
      const eligible = liveVictim
        && this.hasEligibleResidence(liveVictim, ['clean'], visibleAuthorization)
        && receipt.draftId === before.draftId
        && receipt.version === before.version
        && after.draftId === receipt.draftId
        && after.version === receipt.version
        && after.savedVersion >= receipt.version;
      if (!eligible) {
        throw this.error(
          request,
          before.draftId,
          `victimConversationId=${victim.conversationId} became ineligible after checkpoint ACK; retry`,
        );
      }
      if (intent.revision !== selectedIntentRevision) return false;
    } catch (error) {
      const after = controller.getSnapshot();
      if (after.status === 'error') {
        this.port.transition(victim.conversationId, { axis: 'checkpoint', value: 'error' });
        this.checkpointRetryByTarget.set(target.conversationId, {
          victimConversationId: victim.conversationId,
          draftId: before.draftId,
          version: before.version,
          visibleAuthorization,
        });
      } else if (after.savedVersion < after.version) {
        this.port.transition(victim.conversationId, { axis: 'checkpoint', value: 'dirty' });
      }
      if (error instanceof Error && error.message.includes('became ineligible')) throw error;
      throw this.error(
        request,
        before.draftId,
        `checkpoint of victimConversationId=${victim.conversationId} failed; keep it open and retry`,
        error,
      );
    }
    const wasVisible = visibleAuthorization !== null;
    if (wasVisible) this.port.maskVisible(victim.conversationId);
    if (!this.port.coolPhysical(victim.conversationId, 'capacity LRU checkpointed')) {
      if (wasVisible) this.port.selectVisible(victim.conversationId);
      throw this.error(request, before.draftId, `victimConversationId=${victim.conversationId} vanished before physical cooling; retry`);
    }
    this.checkpointRetryByTarget.delete(target.conversationId);
    this.port.transition(victim.conversationId, { axis: 'temperature', value: 'cold' });
    return true;
  }

  private async restoreCooledResident(victim: CanvasSessionSnapshot): Promise<void> {
    const seed = this.port.getLogicalSeed(victim.conversationId);
    if (!seed) {
      throw new Error(
        `Canvas outgoing rollback lost conversationId=${victim.conversationId}: logical seed is missing.`,
      );
    }
    const mounted = this.port.mountColdTarget({
      conversationId: victim.conversationId,
      seed,
    });
    this.port.transition(victim.conversationId, { axis: 'temperature', value: 'hydrating' });
    // Re-select immediately so the slot cannot project the failed target while
    // React reconnects the preserved DOCX to a fresh SuperDoc epoch.
    this.port.selectVisible(victim.conversationId);
    await this.port.waitForExactReady(mounted, this.hydrationTimeoutMs);
    this.port.transition(victim.conversationId, { axis: 'temperature', value: 'hot' });
    this.port.touch(victim.conversationId);
  }

  private hasEligibleResidence(
    session: CanvasSessionSnapshot,
    acceptedCheckpoints: readonly CanvasSessionCheckpoint[],
    visibleAuthorization: ExactOutgoingResident | null = null,
  ): boolean {
    const visibilityEligible = session.visibility === 'masked'
      || (
        session.visibility === 'visible'
        && this.sameOutgoing(visibleAuthorization, session)
        && this.port.getRegistrySnapshot().visibleConversationId === session.conversationId
      );
    if (
      session.temperature !== 'hot'
      || !visibilityEligible
      || isCanvasSessionProtected(session)
      || !acceptedCheckpoints.includes(session.checkpoint)
    ) return false;
    const controller = this.port.getDraftController(session.conversationId)?.getSnapshot();
    return Boolean(
      controller?.draftId
      && this.port.getScopeSnapshot(session.conversationId)?.state === 'ready',
    );
  }

  /**
   * Ce résident visible est-il EXACTEMENT celui que le geste s'est autorisé ?
   *
   * ⚠️ La conjonction ne compte plus que deux champs (#1336), et elle n'a rien
   * perdu : `documentSessionId` était le troisième, et il désignait le même fait
   * que `conversationId` désormais clé. L'époque reste indispensable — c'est
   * elle, et elle seule, qui distingue le sortant capturé d'un remontage du même
   * document survenu pendant l'attente.
   */
  private sameOutgoing(
    expected: ExactOutgoingResident | null,
    live: Pick<CanvasSessionSnapshot, 'conversationId' | 'mountEpoch'> | null,
  ): boolean {
    return Boolean(
      expected
      && live
      && expected.conversationId === live.conversationId
      && expected.mountEpoch === live.mountEpoch
    );
  }

  private error(
    request: CanvasResidentRequest,
    draftId: string | null,
    cause: string,
    error?: unknown,
  ): Error {
    const detail = error instanceof Error ? ` Detail: ${error.message}` : '';
    return new Error(
      `Canvas residence request failed for conversationId=${request.conversationId} `
      + `draftId=${draftId ?? 'none'}: ${cause}.${detail}`,
    );
  }

  private unavailable(
    request: CanvasResidentRequest,
    kind: CanvasResidenceUnavailableKind,
    reason: string,
  ): CanvasResidenceUnavailableError {
    return new CanvasResidenceUnavailableError(request.conversationId, kind, reason);
  }
}
